import { CommonModule } from '@angular/common';
import {
  Component,
  signal,
  input,
  output,
  computed,
  OnInit,
  ChangeDetectionStrategy,
} from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import { FormFieldComponent } from './form-field/form-field.component';
import { FormField, SelectOption } from '../../../../models/form.models';

@Component({
  selector: 'app-main-info',
  standalone: true,
  imports: [CommonModule, TranslatePipe, FormFieldComponent],
  templateUrl: './main-info.component.html',
  styleUrl: './main-info.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MainInfoComponent implements OnInit {
  /** Admin layout: show free-text topic field. */
  showTopic = input(false);

  topicText = signal('');

  ngOnInit(): void {
    // Emit initial data on component initialization
    this.emitDataChange();
  }

  onTopicInput(value: string): void {
    this.topicText.set(value);
    this.emitDataChange();
  }
  // Discipline options - Kid-friendly subjects that explain complex topics simply
  private disciplineOptions: SelectOption[] = [
    { value: 'cybersecurity-safety', translationKey: 'cybersecuritySafety' },
    { value: 'internet-safety', translationKey: 'internetSafety' },
    { value: 'online-privacy', translationKey: 'onlinePrivacy' },
    { value: 'digital-citizenship', translationKey: 'digitalCitizenship' },
    { value: 'coding-programming', translationKey: 'codingProgramming' },
    { value: 'robotics', translationKey: 'robotics' },
    { value: 'science-experiments', translationKey: 'scienceExperiments' },
    { value: 'mathematics', translationKey: 'mathematics' },
    { value: 'reading-literacy', translationKey: 'readingLiteracy' },
    { value: 'writing-creativity', translationKey: 'writingCreativity' },
    { value: 'history-stories', translationKey: 'historyStories' },
    { value: 'geography-world', translationKey: 'geographyWorld' },
    { value: 'art-creativity', translationKey: 'artCreativity' },
    { value: 'music-sounds', translationKey: 'musicSounds' },
    { value: 'nature-environment', translationKey: 'natureEnvironment' },
    { value: 'animals-wildlife', translationKey: 'animalsWildlife' },
    { value: 'space-astronomy', translationKey: 'spaceAstronomy' },
    { value: 'health-wellness', translationKey: 'healthWellness' },
    { value: 'emotions-feelings', translationKey: 'emotionsFeelings' },
    { value: 'friendship-social', translationKey: 'friendshipSocial' },
    { value: 'problem-solving', translationKey: 'problemSolving' },
    { value: 'critical-thinking', translationKey: 'criticalThinking' },
    { value: 'money-finance', translationKey: 'moneyFinance' },
    { value: 'cooking-nutrition', translationKey: 'cookingNutrition' },
    { value: 'sports-fitness', translationKey: 'sportsFitness' },
  ];

  // Audience options - Age groups for kids
  private audienceOptions: SelectOption[] = [
    { value: 'preschool-3-5', translationKey: 'preschool35' },
    { value: 'kindergarten-5-6', translationKey: 'kindergarten56' },
    { value: 'early-elementary-6-8', translationKey: 'earlyElementary68' },
    { value: 'elementary-8-10', translationKey: 'elementary810' },
    { value: 'late-elementary-10-12', translationKey: 'lateElementary1012' },
    { value: 'middle-school-12-14', translationKey: 'middleSchool1214' },
    { value: 'early-teens-14-16', translationKey: 'earlyTeens1416' },
    { value: 'mixed-ages', translationKey: 'mixedAges' },
    { value: 'beginners', translationKey: 'beginners' },
    { value: 'intermediate-learners', translationKey: 'intermediateLearners' },
    { value: 'advanced-kids', translationKey: 'advancedKids' },
  ];

  // Tone of voice options - Kid-friendly and simple
  private toneOptions: SelectOption[] = [
    { value: 'fun-playful', translationKey: 'funPlayful' },
    { value: 'friendly-warm', translationKey: 'friendlyWarm' },
    { value: 'simple-clear', translationKey: 'simpleClear' },
    {
      value: 'encouraging-supportive',
      translationKey: 'encouragingSupportive',
    },
    { value: 'adventurous-exciting', translationKey: 'adventurousExciting' },
    { value: 'curious-wondering', translationKey: 'curiousWondering' },
    { value: 'gentle-patient', translationKey: 'gentlePatient' },
    {
      value: 'enthusiastic-energetic',
      translationKey: 'enthusiasticEnergetic',
    },
    {
      value: 'storytelling-narrative',
      translationKey: 'storytellingNarrative',
    },
    {
      value: 'educational-informative',
      translationKey: 'educationalInformative',
    },
  ];

  fields = signal<FormField[]>([
    {
      id: 'discipline',
      labelKey:
        'app.components.courseCreator.settings.mainInfo.discipline.label',
      placeholderKey:
        'app.components.courseCreator.settings.mainInfo.discipline.placeholder',
      type: 'select',
      value: '',
      options: this.disciplineOptions.map((opt) => opt.value),
      customValue: '',
    },
    {
      id: 'audience',
      labelKey: 'app.components.courseCreator.settings.mainInfo.audience.label',
      placeholderKey:
        'app.components.courseCreator.settings.mainInfo.audience.placeholder',
      type: 'select',
      value: '',
      options: this.audienceOptions.map((opt) => opt.value),
      customValue: '',
    },
    {
      id: 'tone',
      labelKey: 'app.components.courseCreator.settings.mainInfo.tone.label',
      placeholderKey:
        'app.components.courseCreator.settings.mainInfo.tone.placeholder',
      type: 'select',
      value: '',
      options: this.toneOptions.map((opt) => opt.value),
      customValue: '',
    },
  ]);

  // Cache options for each field to avoid recalculating on every change detection
  private readonly optionsCache = new Map<string, SelectOption[]>();

  // Get options for a specific field (including "Other") - cached
  getOptionsForField(fieldId: string): SelectOption[] {
    // Return cached value if available
    if (this.optionsCache.has(fieldId)) {
      return this.optionsCache.get(fieldId)!;
    }

    let baseOptions: SelectOption[] = [];
    switch (fieldId) {
      case 'discipline':
        baseOptions = this.disciplineOptions;
        break;
      case 'audience':
        baseOptions = this.audienceOptions;
        break;
      case 'tone':
        baseOptions = this.toneOptions;
        break;
      default:
        return [];
    }
    // Add "Other" option at the end
    const options = [
      ...baseOptions,
      { value: 'other', translationKey: 'other' },
    ];
    // Cache the result
    this.optionsCache.set(fieldId, options);
    return options;
  }

  // Handle selection change
  onSelectionChange(event: { fieldId: string; value: string }): void {
    const fields = this.fields();
    const fieldIndex = fields.findIndex((f) => f.id === event.fieldId);
    if (fieldIndex !== -1) {
      const updatedFields = [...fields];
      const field = { ...updatedFields[fieldIndex] };

      // If "other" is selected, keep customValue, otherwise clear it
      if (event.value === 'other') {
        field.value = event.value;
        // Keep existing customValue or set empty string
        field.customValue = field.customValue || '';
      } else {
        field.value = event.value;
        field.customValue = ''; // Clear custom value when selecting a regular option
      }

      updatedFields[fieldIndex] = field;
      this.fields.set(updatedFields);
      this.emitOnSelectionChange();
    }
  }

  // Handle custom value change
  onCustomValueChange(event: { fieldId: string; customValue: string }): void {
    const fields = this.fields();
    const fieldIndex = fields.findIndex((f) => f.id === event.fieldId);
    if (fieldIndex !== -1) {
      const updatedFields = [...fields];
      updatedFields[fieldIndex] = {
        ...updatedFields[fieldIndex],
        customValue: event.customValue,
      };
      this.fields.set(updatedFields);
      this.emitDataChange();
    }
  }

  // Output to emit data changes to parent
  dataChange = output<{
    discipline: string;
    audience: string;
    tone: string;
    topic?: string;
  }>();

  // Computed value for current data
  private currentData = computed(() => {
    const fields = this.fields();
    const disciplineField = fields.find((f) => f.id === 'discipline');
    const audienceField = fields.find((f) => f.id === 'audience');
    const toneField = fields.find((f) => f.id === 'tone');

    // If "other" is selected, use custom value; otherwise use the selected value
    const getFieldValue = (field: FormField | undefined): string => {
      if (!field) return '';
      if (field.value === 'other') {
        return field.customValue || '';
      }
      return field.value;
    };

    const base = {
      discipline: getFieldValue(disciplineField),
      audience: getFieldValue(audienceField),
      tone: getFieldValue(toneField),
    };
    if (this.showTopic()) {
      return { ...base, topic: this.topicText().trim() };
    }
    return base;
  });

  // Emit data change
  private emitDataChange(): void {
    this.dataChange.emit(this.currentData());
  }

  // Emit initial data and on selection change
  private emitOnSelectionChange(): void {
    this.emitDataChange();
  }
}
